import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { toast } from 'sonner';
import { auth as authApi, terms as termsApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { EmailAuthForm } from './EmailAuth';
import { Phone, KeyRound, ScrollText, LogIn, UserPlus, Store as StoreIcon, User, Mail } from 'lucide-react';
import PlatformTermsModal from './PlatformTermsModal';

/**
 * Two-tab auth modal:
 *  • "Sign in" — minimal, blue accent, existing users. Sends OTP straight away.
 *  • "Sign up" — orange accent, requires role choice + T&C acceptance.
 * Both funnel into the same OTP verification step.
 */
export default function AuthModal({ open, onOpenChange, initialMode = 'signin' }) {
  const [mode, setMode] = useState(initialMode);       // 'signin' | 'signup'
  const [step, setStep] = useState('phone');        // 'phone' | 'otp'
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('customer');
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [devCode, setDevCode] = useState(null);
  const [useEmail, setUseEmail] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);
  const { login } = useAuth();

  // Sync when the launcher re-opens the modal with a specific tab
  useEffect(() => {
    if (open) { setMode(initialMode); setStep('phone'); }
  }, [open, initialMode]);

  const reset = () => {
    setMode('signin'); setStep('phone'); setPhone(''); setEmail(''); setCode('');
    setName(''); setRole('customer'); setAgreed(false); setDevCode(null);
  };

  const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);

  const sendOtp = async () => {
    if (!/^\d{10}$/.test(phone)) { toast.error('Enter a 10-digit phone number'); return; }
    if (isSignup && email && !emailValid(email)) { toast.error('Enter a valid email address'); return; }
    if (mode === 'signup' && !agreed) { toast.error('Please accept the Platform Terms to sign up'); return; }
    setBusy(true);
    try {
      const r = await authApi.sendOtp(phone, isSignup ? (email || undefined) : undefined);
      setCooldown(30);
      if (r.dev_code) {
        setDevCode(r.dev_code);
        toast.success(`Preview mode — your code is ${r.dev_code}`);
      } else if (!r.sent) {
        toast.error(r.hint || 'SMS is not connected yet');
        return;
      } else {
        toast.success('OTP sent by SMS');
      }
      setStep('otp');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send OTP'); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (!/^\d{6}$/.test(code)) { toast.error('Enter a 6-digit code'); return; }
    setBusy(true);
    try {
      const payload = { phone, code };
      if (email) payload.email = email;
      if (mode === 'signup') {
        payload.name = name || undefined;
        payload.role = role;
      }
      const { token, user } = await authApi.verifyOtp(payload);
      login(token, user);
      if (mode === 'signup') {
        try { await termsApi.acceptPlatform(); } catch { /* non-blocking */ }
      }
      toast.success(`Welcome, ${user.name}`);
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification failed');
    } finally { setBusy(false); }
  };

  const isSignup = mode === 'signup';
  const accent = isSignup ? '#E67E22' : '#0D47A1';    // orange for signup, blue for signin
  const accentDark = isSignup ? '#D35400' : '#0A3272';
  const accentSoft = isSignup ? '#FFF7EE' : '#E3F2FD';

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent data-testid="auth-modal" className="sm:max-w-md font-body p-0 overflow-hidden">
        {/* Colored header — flips theme based on tab */}
        <div className="px-6 pt-5 pb-4 transition-colors" style={{ background: accentSoft }}>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl" style={{ color: accentDark }}>
              {isSignup ? 'Create your BharatCart account' : 'Welcome back to BharatCart'}
            </DialogTitle>
            <DialogDescription>
              {isSignup
                ? 'Set up your account, then verify with a one-time code.'
                : useEmail ? 'Log in with your email and password.' : "Enter your phone — we'll text you a one-time code."}
            </DialogDescription>
          </DialogHeader>
          {step === 'phone' && (
            <div className="mt-4 flex gap-1 p-1 bg-white/70 rounded-full border border-border/60 shadow-inner">
              <button data-testid="auth-tab-signin" onClick={() => { setMode('signin'); setStep('phone'); }}
                className={`flex-1 inline-flex items-center justify-center gap-1 rounded-full py-1.5 text-sm font-bold transition
                  ${!isSignup ? 'bg-[#0D47A1] text-white shadow' : 'text-ink/60 hover:text-[#0D47A1]'}`}>
                <LogIn className="w-3.5 h-3.5"/>Sign in
              </button>
              <button data-testid="auth-tab-signup" onClick={() => { setMode('signup'); setStep('phone'); }}
                className={`flex-1 inline-flex items-center justify-center gap-1 rounded-full py-1.5 text-sm font-bold transition
                  ${isSignup ? 'bg-[#E67E22] text-white shadow' : 'text-ink/60 hover:text-[#D35400]'}`}>
                <UserPlus className="w-3.5 h-3.5"/>Sign up
              </button>
            </div>
          )}
        </div>

        <div className="px-6 py-5">
          {step === 'phone' && (
            <button type="button" data-testid="auth-toggle-email" onClick={() => setUseEmail(!useEmail)}
              className="mb-3 text-xs font-bold text-[#0D47A1] underline">
              {useEmail ? 'Use phone code instead' : 'Use email & password instead'}
            </button>
          )}
          {step === 'phone' && useEmail && (
            <EmailAuthForm isSignup={isSignup} onDone={(token, u) => { login(token, u); toast.success(`Welcome, ${u.name || 'friend'}!`); onOpenChange(false); }}/>
          )}
          {step === 'phone' && !useEmail && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="phone-input" className="text-xs uppercase tracking-[0.18em] font-bold">Mobile number</Label>
                <div className="mt-2 flex items-center gap-2 rounded-lg border border-border bg-white pl-3">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">+91</span>
                  <Input data-testid="phone-input" id="phone-input" value={phone}
                    onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="98765 43210"
                    className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0" />
                </div>
              </div>

              {isSignup && (
                <>
                  <div>
                    <Label htmlFor="email-input" className="text-xs uppercase tracking-[0.18em] font-bold">Email (recommended)</Label>
                    <div className="mt-2 flex items-center gap-2 rounded-lg border border-border bg-white pl-3">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <Input data-testid="email-input" id="email-input" type="email"
                        value={email} onChange={e => setEmail(e.target.value.trim())}
                        placeholder="you@example.com"
                        className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"/>
                    </div>
                    <div className="mt-1 text-[11px] text-ink/60">
                      We&apos;ll mirror the OTP to your email so you can log in even when SMS is delayed.
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.18em] font-bold">I am a</Label>
                    <RadioGroup value={role} onValueChange={setRole} className="mt-2 grid grid-cols-2 gap-2">
                      <label data-testid="role-customer"
                        className={`cursor-pointer rounded-lg border p-3 text-sm flex items-center gap-2 transition
                          ${role === 'customer' ? 'border-[#E67E22] bg-[#FFF7EE]' : 'border-border bg-white hover:border-[#E67E22]/60'}`}>
                        <RadioGroupItem value="customer" />
                        <User className="w-4 h-4"/> Customer
                      </label>
                      <label data-testid="role-owner"
                        className={`cursor-pointer rounded-lg border p-3 text-sm flex items-center gap-2 transition
                          ${role === 'owner' ? 'border-[#E67E22] bg-[#FFF7EE]' : 'border-border bg-white hover:border-[#E67E22]/60'}`}>
                        <RadioGroupItem value="owner" />
                        <StoreIcon className="w-4 h-4"/> Store Owner
                      </label>
                    </RadioGroup>
                    <div className="mt-2 text-[11px] text-ink/60">
                      Delivery Partner &amp; Supply-Chain roles use a separate flow after sign-up.
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.18em] font-bold">Name (optional)</Label>
                    <Input data-testid="name-input" value={name} onChange={e => setName(e.target.value)}
                      placeholder="Your name" className="mt-2" />
                  </div>

                  <label data-testid="auth-terms-label"
                    className={`flex items-start gap-2 rounded-lg border p-3 text-[12px] leading-snug transition
                      ${agreed ? 'border-[#A5D6A7] bg-[#F1F8E9]' : 'border-[#F2A157] bg-[#FFF7EE]'}`}>
                    <input data-testid="auth-terms-check" type="checkbox" checked={agreed}
                      onChange={e => setAgreed(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-[#E67E22] shrink-0"/>
                    <span className="text-ink/80">
                      I accept the BharatCart
                      <button type="button" data-testid="auth-terms-link" onClick={() => setTermsOpen(true)}
                        className="underline font-semibold text-[#D35400] inline-flex items-center gap-1 ml-1">
                        <ScrollText className="w-3 h-3"/>Platform Terms &amp; Conditions
                      </button>
                      , including Indian business laws (Consumer Protection Act 2019, DPDP Act 2023, IT Act 2000, GST Act 2017{role === 'owner' && ', Shops & Establishments Act, FSSAI'}) and exclusive <b>Hyderabad, Telangana</b> jurisdiction. I am 18+ and will not list or order any illegal goods.
                    </span>
                  </label>
                </>
              )}

              {!isSignup && (
                <div className="text-[11px] text-ink/60 rounded-lg border border-[#0D47A1]/20 bg-[#E3F2FD]/40 p-3">
                  New to BharatCart?
                  <button data-testid="auth-switch-to-signup" onClick={() => setMode('signup')}
                    className="ml-1 font-semibold text-[#0D47A1] hover:underline inline-flex items-center gap-1">
                    <UserPlus className="w-3 h-3"/>Create an account
                  </button>
                </div>
              )}

              <Button data-testid="send-otp-button" onClick={sendOtp}
                disabled={busy || cooldown > 0 || (isSignup && !agreed)}
                className="w-full text-white rounded-full h-11 hover:opacity-95"
                style={{ background: accent }}>
                {cooldown > 0 ? `Resend in ${cooldown}s` : <>
                {isSignup
                  ? <><UserPlus className="w-4 h-4 mr-2"/>Create account &amp; send OTP</>
                  : <><LogIn className="w-4 h-4 mr-2"/>Send OTP &amp; sign in</>
                }</>}
              </Button>
            </div>
          )}

          {step === 'otp' && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Code sent to +91 {phone}{email ? <> · also to <b>{email}</b></> : null}
              </div>
              {devCode && (
                <div data-testid="otp-dev-code" className="rounded-lg bg-[#FFF3E0] text-[#9A4500] text-xs font-bold px-3 py-2 flex items-center justify-between gap-2">
                  <span>Preview only (SMS not connected): code <span className="font-mono tracking-[0.3em]">{devCode}</span></span>
                  <button type="button" data-testid="otp-dev-code-fill" onClick={() => setCode(devCode)} className="underline">Fill</button>
                </div>
              )}
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] font-bold">6-digit OTP</Label>
                <div className="mt-2 flex items-center gap-2 rounded-lg border border-border bg-white pl-3">
                  <KeyRound className="w-4 h-4 text-muted-foreground" />
                  <Input data-testid="otp-input" value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="••••••"
                    className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 tracking-[0.4em] font-mono" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button data-testid="change-number-button" variant="outline" onClick={() => setStep('phone')}
                  className="rounded-full">Change number</Button>
                <Button data-testid="verify-otp-button" onClick={verify} disabled={busy}
                  className="flex-1 text-white rounded-full hover:opacity-95"
                  style={{ background: accent }}>
                  Verify &amp; {isSignup ? 'create account' : 'sign in'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <PlatformTermsModal open={termsOpen} onOpenChange={setTermsOpen}/>
    </Dialog>
  );
}
